import type { BoardDefinition } from '@skyrover/shared/types';
import { getVariableCppType } from '@skyrover/blockly-blocks';
import { CodeGenerator, Names } from 'blockly/core';
import type { Block } from 'blockly/core';
import type { Workspace } from 'blockly/core';

import { resetBeginnerLedCodegenState } from './beginner-led-state.js';
import { resetBeginnerDisplayCodegenState } from './beginner-display-state.js';
import { resetBeginnerTimeCodegenState } from './beginner-time-state.js';

/** Operator precedence for Arduino C++ code generation */
export enum Order {
  ATOMIC = 0,
  FUNCTION_CALL = 1,
  UNARY = 2,
  MULTIPLICATIVE = 3,
  ADDITIVE = 4,
  RELATIONAL = 5,
  LOGICAL_NOT = 6,
  LOGICAL_AND = 7,
  LOGICAL_OR = 8,
  CONDITIONAL = 9,
  NONE = 99,
}

export interface GeneratedSketch {
  code: string;
  includes: string[];
  boardId: string;
}

export interface BoardGeneratorExtension {
  getPreamble?(board: BoardDefinition): string[];
  getSetupPrefix?(board: BoardDefinition): string[];
  getLoopSuffix?(board: BoardDefinition): string[];
}

const boardExtensions = new Map<string, BoardGeneratorExtension>();

export function registerBoardGeneratorExtension(
  boardId: string,
  extension: BoardGeneratorExtension,
): void {
  boardExtensions.set(boardId, extension);
}

export function getBoardGeneratorExtension(boardId: string): BoardGeneratorExtension | undefined {
  return boardExtensions.get(boardId);
}

const PROGRAM_BLOCK_TYPES = new Set([
  'arduino_program',
  'arduino_setup',
  'arduino_loop',
  'beginner_start',
]);

/** Top-level blocks that declare functions but are not executable statements. */
const TOP_LEVEL_DEFINITION_TYPES = new Set([
  'procedures_defreturn',
  'procedures_defnoreturn',
]);

export class ArduinoGenerator extends CodeGenerator {
  includes = new Set<string>();
  globals = new Set<string>();
  setupStatements: string[] = [];
  loopStatements: string[] = [];
  setupOnceKeys = new Set<string>();
  activeBoard: BoardDefinition | null = null;

  constructor() {
    super('Arduino');
    this.addReservedWords('setup,loop,Serial,if,else,for,while,do,break,continue,return');
    this.isInitialized = false;
  }

  override init(workspace: Workspace): void {
    super.init(workspace);
    this.nameDB_ = new Names(this.RESERVED_WORDS_);
    this.nameDB_.setVariableMap(workspace.getVariableMap());
    this.nameDB_.populateVariables(workspace);
    this.nameDB_.populateProcedures(workspace);
    this.isInitialized = true;
  }

  declareWorkspaceVariables(workspace: Workspace): void {
    if (!this.nameDB_) {
      return;
    }

    for (const variable of workspace.getVariableMap().getAllVariables()) {
      const varName = this.nameDB_.getName(variable.getId(), Names.NameType.VARIABLE);
      this.addGlobal(`${getVariableCppType(variable.getType())} ${varName};`);
    }
  }

  override scrub_(block: Block, code: string, opt_thisOnly?: boolean): string {
    const nextBlock = block.nextConnection?.targetBlock();
    const nextCode = opt_thisOnly ? '' : (nextBlock ? this.blockToCode(nextBlock) : '');
    return code + nextCode;
  }

  addInclude(header: string): void {
    this.includes.add(header);
  }

  addGlobal(line: string): void {
    this.globals.add(line);
  }

  addSetup(line: string): void {
    if (line.trim()) {
      this.setupStatements.push(line);
    }
  }

  addSetupOnce(key: string, line: string): void {
    if (this.setupOnceKeys.has(key) || !line.trim()) {
      return;
    }
    this.setupOnceKeys.add(key);
    this.setupStatements.push(line);
  }

  addLoop(line: string): void {
    if (line.trim()) {
      this.loopStatements.push(line);
    }
  }

  generateSketch(workspace: Workspace, board: BoardDefinition): GeneratedSketch {
    this.activeBoard = board;
    this.includes.clear();
    this.globals.clear();
    this.setupStatements = [];
    this.loopStatements = [];
    this.setupOnceKeys.clear();
    resetBeginnerLedCodegenState();
    resetBeginnerDisplayCodegenState();
    resetBeginnerTimeCodegenState();

    this.init(workspace);
    this.declareWorkspaceVariables(workspace);
    this.addInclude('#include <Arduino.h>');

    const extension = getBoardGeneratorExtension(board.id);

    const topBlocks = workspace.getTopBlocks(true);
    for (const block of topBlocks) {
      if (PROGRAM_BLOCK_TYPES.has(block.type) || TOP_LEVEL_DEFINITION_TYPES.has(block.type)) {
        this.blockToCode(block);
      }
    }

    const preamble = extension?.getPreamble?.(board) ?? [];
    const setupPrefix = extension?.getSetupPrefix?.(board) ?? [];
    const loopSuffix = extension?.getLoopSuffix?.(board) ?? [];

    const includeBlock = [...this.includes]
      .sort()
      .map((inc) => `${inc}\n`)
      .join('');

    const globalsBlock = [...this.globals].map((g) => `${g}\n`).join('');
    const preambleBlock = preamble.map((l) => `${l}\n`).join('');

    const setupBody = [...setupPrefix, ...this.setupStatements]
      .map((s) => `  ${s.trim().replace(/\n/g, '\n  ')}\n`)
      .join('');

    const loopBody = [...this.loopStatements, ...loopSuffix]
      .map((s) => `  ${s.trim().replace(/\n/g, '\n  ')}\n`)
      .join('');

    const header = [
      '// Generated by Skyrover IDE',
      `// Board: ${board.displayName} (${board.id})`,
      `// FQBN: ${board.fqbn}`,
      '',
    ].join('\n');

    const code = [
      header,
      includeBlock,
      preambleBlock,
      globalsBlock,
      'void setup() {',
      setupBody || '  // no setup code\n',
      '}',
      '',
      'void loop() {',
      loopBody || '  // no loop code\n',
      '}',
      '',
    ].join('\n');

    return {
      code,
      includes: [...this.includes],
      boardId: board.id,
    };
  }
}

export const arduinoGenerator = new ArduinoGenerator();

export function quoteString(value: string): string {
  return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n')}"`;
}

export function quoteChar(value: string): string {
  const char = value.length > 0 ? value.slice(0, 1) : ' ';
  if (char === '\\') {
    return "'\\\\'";
  }
  if (char === "'") {
    return "'\\''";
  }
  return `'${char}'`;
}

export function blockTraceComment(block: Block): string {
  return `// BLOCK: ${block.type}`;
}
